import React, { useState, useEffect } from "react";
import {
  FaIdCard,
  FaSignInAlt,
  FaUniversity,
  FaFingerprint,
} from "react-icons/fa";
import { MdHourglassEmpty } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import FinoDmtService from "../../../services/FinoDmtService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import Swal from "sweetalert2";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";

const BcAgentOnboarding = () => {
  const [loading, setLoading] = useState(false);
  const [isSubChecked, setIsSubChecked] = useState(false);

  const navigate = useNavigate();

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  /* -------------------------------
      INIT
  -------------------------------- */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await checkSubscriptionStatus();
        setIsSubChecked(true); // ✅ important
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  /* -------------------------------
      HANDLERS
  -------------------------------- */
  const handleMerchantOnboarding = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const result = await FinoDmtService.generateMerchantOnboardUrl();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        const url = res?.data?.url;
        window.open(url, "_blank");
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------
      SERVICES
  -------------------------------- */
  const services = [
    {
      name: "Merchant Onboarding",
      icon: <FaIdCard size={40} color="#0d6efd" />,
      action: handleMerchantOnboarding,
    },
    {
      name: "Merchant Onboarding Status",
      icon: <MdHourglassEmpty size={40} color="#0d6efd" />,
      action: () => navigate("/bc-agent-onboarding/merchant-onboarding-status"),
    },
    {
      name: "E-KYC",
      subTitle: "Fino Payments Bank",
      icon: <FaFingerprint size={40} color="#0d6efd" />,
      action: () => navigate("/bc-agent-onboarding/fino-ekyc"),
    },
    // {
    //   name: "E-KYC",
    //   subTitle: "Jio Payments Bank",
    //   icon: <FaFingerprint size={40} color="#0d6efd" />,
    //   action: () => navigate("/bc-agent-onboarding/jio-ekyc"),
    // },
    // {
    //   name: "E-KYC",
    //   subTitle: "NSDL Payments Bank",
    //   icon: <FaFingerprint size={40} color="#0d6efd" />,
    //   action: () => navigate("/bc-agent-onboarding/merchant-ekyc"),
    // },
  ];

  /* -------------------------------
      CLICK HANDLER (FIXED)
  -------------------------------- */
  const handleClick = (service) => {
    if (loading) return;

    // ⛔ wait until subscription loaded
    if (!isSubChecked) return;

    // ❌ not subscribed
    if (premiumStatus !== "true") {
      setShowDialog(true);
      return;
    }

    // ✅ allowed
    service.action();
  };

  return (
    <div>
      {loading && <LoadingScreen />}

      <div className="container-fluid mt-3">
        <div className="row">
          <SideBar />

          <div className="col-md-9 mb-3">
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">BC Agent Onboarding</h5>

                <div className="row justify-content-center">
                  <div className="d-flex gap-4 flex-wrap justify-content-center">
                    {services.map((service, index) => (
                      <div
                        key={index}
                        className="border rounded p-4 text-center shadow-sm hover-effect"
                        style={{
                          width: "220px",
                          cursor:
                            loading || !isSubChecked
                              ? "not-allowed"
                              : "pointer",
                          opacity: loading || !isSubChecked ? 0.6 : 1,
                          transition: "all 0.3s ease",
                        }}
                        onClick={() => handleClick(service)}
                        onMouseEnter={(e) =>
                          !loading &&
                          isSubChecked &&
                          (e.currentTarget.style.transform = "scale(1.05)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      >
                        <div className="mb-3">{service.icon}</div>

                        <div style={{ textAlign: "center" }}>
                          <div
                            className="fw-medium"
                            style={{ fontSize: "16px", fontWeight: "500" }}
                          >
                            {service.name}
                          </div>

                          {service.subTitle && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              {service.subTitle}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Dialog */}
      <SubscriptionDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        serviceName="BC Agent Onboarding"
      />
    </div>
  );
};

export default BcAgentOnboarding;
